import React, { PureComponent } from 'react';
import { isString, isNil, get, isEmpty, values } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import GoogleMapReact from 'google-map-react';
import ReactGoogleMapLoader from 'react-google-maps-loader';
import Autosuggest from 'react-autosuggest';
import { Popper, Paper } from '@mui/material';

import APIClient from '../../services/APIClient';
import { transformKeysFromCamelCaseToSnakeCase } from '../../services/ObjectUtils';
import { defaultGmapProps, dashboardMapKeys } from '../../../screens/Dashboard/components/GMap';

import { Dialog } from '../Dialog/Dialog';
import Button from '../Button/Button';
import MenuItem from '../MenuItem/MenuItem';
import { autosuggestStyles } from './RenderAutocomplete';
import TextField from '../TextField/TextField';

const noAddressMsg = 'Address was not found.';
const addressZoom = 18;
const options = { fullscreenControl: false };

const validateCoordinate = (coordVal) => (isString(coordVal) && coordVal.length ? +coordVal : coordVal === '' ? undefined : coordVal);

const mapDialogErrorStyle = {
  position: 'absolute',
  color: 'red',
  height: '51px',
  verticalAlign: 'middle',
  alignItems: 'center',
  textAlign: 'center',
  display: 'inline-flex',
  paddingLeft: '10px',
  maxWidth: '60%',
};

class MapDialog extends PureComponent {
  google = null;

  marker = null;

  searchBox = null;

  geocoder = null;

  infowindow = null;

  addressAutosuggestRef = React.createRef();

  state = {
    addressInputValue: '',
    addressSuggestions: [],
    place: null,
    isCountryValid: true,
    geometry: {
      lat: null,
      lng: null,
    },
  };

  setMarkerWithInfo = (markerObj, location, text) => {
    const locationPickerMarkerCountryNames = get(location, 'address_components', {}).find((item) => {
      const types = get(item, 'types', []);

      return types.includes('country') || types.includes('COUNTRY');
    });

    const isCountryValid =
      !isEmpty(locationPickerMarkerCountryNames) && values(locationPickerMarkerCountryNames).includes(this.props.countrySelected);

    this.infowindow.setContent(text);
    this.infowindow.open(this.google.map, markerObj);

    this.setState({
      place: location,
      addressInputValue: text === noAddressMsg ? '' : text,
      addressSuggestions: [],
      isCountryValid,
      geometry: {
        lat: markerObj.position.lat(),
        lng: markerObj.position.lng(),
      },
    });
  };

  onGoogleApiLoaded = (g) => {
    const { countrySelected, c_address: address, addressIsValid, dataSource = [] } = this.props;

    this.google = g;
    const self = this;

    const foundPlace = dataSource[0];

    const geocoder = new g.maps.Geocoder();
    const infowindow = new g.maps.InfoWindow();
    const latitude = validateCoordinate(this.props.c_latitude);
    const longitude = validateCoordinate(this.props.c_longitude);

    this.geocoder = geocoder;
    this.infowindow = infowindow;

    function initMarkerByCoordinates(latitude, longitude) {
      const coords = new g.maps.LatLng(latitude, longitude);

      self.marker = new g.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: g.map,
      });
      g.map.panTo(coords);
      g.map.setZoom(addressZoom);

      geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
        if (status === 'OK') {
          if (results[0]) {
            self.setMarkerWithInfo(self.marker, results[0], results[0].formatted_address);
          } else {
            self.setMarkerWithInfo(self.marker, { geometry: { location: coords } }, noAddressMsg);
          }
        } else {
          self.setMarkerWithInfo(self.marker, { geometry: { location: coords } }, noAddressMsg);
        }
      });
    }
    function initMarkerOnlyByCountry() {
      geocoder.geocode(
        {
          componentRestrictions: {
            country: countrySelected,
          },
        },
        (results, status) => {
          if (status === 'OK') {
            if (get(results[0], 'geometry.bounds')) {
              g.map.fitBounds(results[0].geometry.bounds);
            } else {
              g.map.panTo(results[0].geometry.location);
              g.map.setZoom(5);
            }
          }
        },
      );
    }
    function initMarkerByAdress(address) {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK') {
          g.map.panTo(results[0].geometry.location);
          g.map.setZoom(addressZoom);
          if (results[0]) {
            self.marker = new g.maps.Marker({
              position: results[0].geometry.location,
              map: g.map,
            });
            self.setMarkerWithInfo(self.marker, results[0], results[0].formatted_address);
          }
        } else {
          initMarkerOnlyByCountry();
        }
      });
    }

    if (countrySelected) {
      if (isNil(latitude) || isNil(longitude)) {
        if (isNil(address) || !addressIsValid) {
          if (!foundPlace) {
            // No any guess about the address
            initMarkerOnlyByCountry();
          } else {
            // Set marker to first place of the multiple choice
            this.setState({ addressInputValue: foundPlace.text });
            initMarkerByAdress(foundPlace.text);
          }
        } else {
          // Set marker by valid address string
          this.setState({ addressInputValue: address });
          initMarkerByAdress(address);
        }
      } else {
        initMarkerByCoordinates(latitude, longitude);
      }
    }

    function handleMapClick() {
      g.map.addListener('click', (e) => {
        if (self.marker == null) {
          self.marker = new g.maps.Marker({
            position: e.latLng,
            map: g.map,
          });
        } else {
          self.marker.setPosition(e.latLng);
        }

        g.map.panTo(e.latLng);

        geocoder.geocode({ location: e.latLng }, (results, status) => {
          if (status === 'OK') {
            if (results[0]) {
              self.setMarkerWithInfo(self.marker, results[0], results[0].formatted_address);
            } else {
              self.setMarkerWithInfo(self.marker, { geometry: { location: e.latLng } }, noAddressMsg);
            }
          } else {
            self.setMarkerWithInfo(self.marker, { geometry: { location: e.latLng } }, noAddressMsg);
          }
        });
      });
    }

    handleMapClick();
  };

  componentWillUnmount() {
    if (this.google && this.searchBox) this.google.maps.event.clearInstanceListeners(this.searchBox);
  }

  onBeforeClose = () => {
    this.setState({ place: null });
  };

  handleCloseModal = () => {
    this.onBeforeClose();
    this.props.handleCloseModal();
  };

  handleOverrideClose = () => {
    this.onBeforeClose();
    this.props.handleOverrideClose();
  };

  onInputChange = (event, { newValue }) => {
    this.setState({ addressInputValue: newValue });
  };

  onSuggestionsFetchRequested = ({ value, reason }) => {
    if (reason === 'input-changed' || reason === 'input-focused') {
      const country = this.props.countrySelected.toLowerCase();
      const searchText = value;

      if (!isEmpty(searchText)) {
        APIClient.get(`/geolocation/predictions?isoCountryCode=${country}&address=${encodeURIComponent(searchText)}`).then((response) => {
          const addressSuggestions = response.data.map(({ placeId, description }) => ({ placeId, description }));

          this.setState({ addressSuggestions });
        });
      }
    }
  };

  onSuggestionSelected = (event, { suggestion }) => {
    APIClient.get(`/geolocation/${suggestion.placeId}`).then((response) => {
      const place = transformKeysFromCamelCaseToSnakeCase(response.data);
      const position = get(place, 'geometry.location');

      if (position) {
        if (this.marker === null) {
          this.marker = new this.google.maps.Marker({
            position,
            map: this.google.map,
          });
        } else {
          this.marker.setPosition(position);
        }

        this.google.map.panTo(position);
        this.google.map.setZoom(addressZoom);
        this.setMarkerWithInfo(this.marker, place, place.formatted_address);
      }
    });
  };

  onSuggestionsClearRequested = () => {};

  getSuggestionValue = ({ description }) => description;

  renderSuggestion = ({ description }, { isHighlighted }) => (
    <MenuItem selected={isHighlighted} style={autosuggestStyles.itemStyle} component="div" title={description}>
      {description}
    </MenuItem>
  );

  renderSuggestionsContainer = (options) => {
    const anchorEl = get(this.addressAutosuggestRef, 'current.input');

    const paperStyle = {
      maxHeight: '500px',
      overflowY: 'auto',
      width: anchorEl ? anchorEl.clientWidth : undefined,
    };

    return (
      <Popper style={{ zIndex: 1400 }} anchorEl={anchorEl} open={Boolean(options.children)}>
        <Paper square {...options.containerProps} style={paperStyle}>
          {options.children}
        </Paper>
      </Popper>
    );
  };

  renderInputComponent = (inputProps) => {
    const { classes, inputRef = () => {}, onBlur, onFocus, ref, ...other } = inputProps;

    return (
      <TextField
        {...other}
        disabled={this.props.disabled || this.props.readOnly}
        fullWidth
        InputProps={{
          onBlur,
          onFocus,
          inputRef: (node) => {
            ref(node);
            inputRef(node);
          },
        }}
      />
    );
  };

  renderAddressInput() {
    return (
      <Autosuggest
        ref={this.addressAutosuggestRef}
        renderInputComponent={this.renderInputComponent}
        suggestions={this.state.addressSuggestions}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionSelected={this.onSuggestionSelected}
        getSuggestionValue={this.getSuggestionValue}
        renderSuggestion={this.renderSuggestion}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        theme={{
          suggestionsContainerOpen: autosuggestStyles.suggestionsContainerOpen,
          suggestionsList: autosuggestStyles.suggestionsList,
          suggestion: autosuggestStyles.suggestion,
        }}
        inputProps={{
          className: 'pac-input',
          placeholder: 'Enter address',
          value: this.state.addressInputValue,
          onChange: this.onInputChange,
        }}
      />
    );
  }

  render() {
    const { onHandleSubmit, handleToggleOverride, overrideOpen, mapOpen } = this.props;
    const { place, isCountryValid, geometry } = this.state;

    const actions = [
      <Button key={uuidv4()} color="secondary" onClick={this.handleCloseModal}>
        Cancel
      </Button>,
      <Button key={uuidv4()} color="primary" onClick={() => onHandleSubmit(place, geometry)} disabled={!place || !isCountryValid}>
        Submit
      </Button>,
    ];

    const overrideActions = [
      <Button
        key={uuidv4()}
        variant="contained"
        color="secondary"
        onClick={() => {
          this.onBeforeClose();
          handleToggleOverride(false, place);
        }}
      >
        No
      </Button>,
      <Button key={uuidv4()} color="primary" onClick={() => handleToggleOverride(true, place, geometry)}>
        Yes
      </Button>,
    ];

    return (
      <>
        <Dialog actions={actions} modal={false} open={mapOpen} maxWidth={false} fullWidth onClose={this.handleCloseModal}>
          <div className="map-dialog-container">
            {this.renderAddressInput()}
            <ReactGoogleMapLoader
              params={dashboardMapKeys}
              render={(googleMaps) =>
                googleMaps && (
                  <GoogleMapReact
                    options={options}
                    yesIWantToUseGoogleMapApiInternals
                    center={defaultGmapProps.center}
                    zoom={defaultGmapProps.zoom}
                    onGoogleApiLoaded={this.onGoogleApiLoaded}
                  />
                )
              }
            />
          </div>
          {place && !isCountryValid && <div style={mapDialogErrorStyle}>Please chose the address that matches selected country.</div>}
        </Dialog>

        <Dialog actions={overrideActions} modal={false} open={overrideOpen} fullWidth onClose={this.handleOverrideClose}>
          <div className="map-dialog-confirm-message">Do you want to replace existing address?</div>
        </Dialog>
      </>
    );
  }
}

export default MapDialog;
